import { useRef, useState } from 'react';
import type { Peer } from '@/canvas/types';
import { initials, useDismiss } from '../kit';
import {
  IconUsers, IconEye, IconTarget, IconLogout, IconShare,
} from '../icons';

/**
 * Who is in the sketchpad.
 *
 * Collapsed to a single orb, because most of the time the answer is "just me"
 * and that does not deserve a strip across the top of the drawing. Tapping it
 * opens the roster: everyone here, what they are up to, and the actions that go
 * with them — jump to their view, follow them, or (if you host) remove them.
 *
 * You only appear in that list once somebody else is here, where the point is
 * comparison. On your own it is an invitation instead.
 */
export default function PresenceBar({
  me, peers, overview, following, onToggleOverview, onGoTo, onFollow, onStopFollowing,
  isHost, onKick, seats, connection, latency, onInvite, canInvite,
}: {
  me: { id: string; username: string; avatarColor: string };
  peers: Peer[];
  overview: boolean;
  following: string | null;
  onToggleOverview: () => void;
  onGoTo: (connectionId: string) => void;
  onFollow: (connectionId: string) => void;
  onStopFollowing: () => void;
  isHost: boolean;
  onKick: (userId: string) => void;
  /** Seats in this pad, from the host's plan — not a constant. */
  seats: number;
  connection: 'connecting' | 'open' | 'reconnecting' | 'closed';
  latency: number;
  onInvite: () => void;
  canInvite: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useDismiss([rootRef], () => { setOpen(false); setExpanded(null); }, open);

  const total = peers.length + 1;
  const alone = peers.length === 0;
  const someoneDrawing = peers.some((p) => p.cursor?.down);

  const close = () => { setOpen(false); setExpanded(null); };

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <button
        type="button"
        className="orb"
        data-active={open || overview}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={alone ? 'Who is here — just you' : `Who is here — ${total} people`}
        title="Who is here"
        style={{ position: 'relative' }}
      >
        <IconUsers />
        {!alone ? (
          <span className="presence-count" data-drawing={someoneDrawing}>{total}</span>
        ) : null}
        {/* Only worth showing when something is wrong. */}
        {connection !== 'open' ? (
          <span
            className="status-dot"
            data-state={connection}
            style={{ position: 'absolute', bottom: 6, right: 6 }}
          />
        ) : null}
      </button>

      {following ? (
        <button type="button" className="hint-strip" style={{ position: 'static', transform: 'none', marginTop: 8 }} onClick={onStopFollowing}>
          Following {peers.find((p) => p.connectionId === following)?.user.username ?? 'someone'} — tap to stop
        </button>
      ) : null}

      {open ? (
        <div className="popover presence-panel">
          <div className="row row--between" style={{ marginBottom: 10 }}>
            <p className="popover__title" style={{ margin: 0 }}>In this sketchpad</p>
            <span className="chip__meta">{total} of {seats}</span>
          </div>

          {alone ? (
            <div className="presence-empty">
              <p style={{ margin: '0 0 10px' }}>
                Just you so far. Share the join code and up to {seats - 1}
                {seats - 1 === 1 ? ' friend' : ' friends'} can
                draw here with you.
              </p>
              {canInvite ? (
                <button
                  type="button"
                  className="btn btn--sm btn--accent btn--block"
                  onClick={() => { close(); onInvite(); }}
                >
                  <IconShare size={16} />
                  Invite someone
                </button>
              ) : (
                <p className="tiny muted" style={{ margin: 0 }}>
                  Ask the host for the code to bring more people in.
                </p>
              )}
            </div>
          ) : (
            <ul className="people">
              <li>
                <div className="person person--me">
                  <span className="chip__dot" style={{ background: me.avatarColor }}>{initials(me.username)}</span>
                  <span className="grow">
                    <span className="person__name">{me.username}</span>
                    <span className="chip__meta">you{isHost ? ' · host' : ''}</span>
                  </span>
                </div>
              </li>

              {peers.map((peer) => {
                const isOpen = expanded === peer.connectionId;
                const isFollowed = following === peer.connectionId;
                return (
                  <li key={peer.connectionId}>
                    <button
                      type="button"
                      className="person"
                      data-active={isOpen || isFollowed}
                      aria-expanded={isOpen}
                      onClick={() => setExpanded(isOpen ? null : peer.connectionId)}
                    >
                      <span
                        className="chip__dot"
                        style={{ background: peer.user.avatarColor, color: peer.user.avatarColor }}
                        data-drawing={!!peer.cursor?.down}
                      >
                        <span style={{ color: '#fff' }}>{initials(peer.user.username)}</span>
                      </span>
                      <span className="grow" style={{ textAlign: 'left' }}>
                        <span className="person__name">{peer.user.username}</span>
                        <span className="chip__meta">
                          {peer.role === 'host' ? 'host' : peer.role === 'spectator' ? 'watching' : 'drawing here'}
                          {isFollowed ? ' · following' : ''}
                        </span>
                      </span>
                    </button>

                    {isOpen ? (
                      <div className="person__actions">
                        <button
                          type="button"
                          className="btn btn--sm btn--ghost"
                          style={{ justifyContent: 'flex-start' }}
                          disabled={!peer.viewport}
                          onClick={() => { close(); onGoTo(peer.connectionId); }}
                        >
                          <IconTarget size={16} />
                          Go to their view
                        </button>
                        <button
                          type="button"
                          className="btn btn--sm btn--ghost"
                          style={{ justifyContent: 'flex-start' }}
                          disabled={!peer.viewport}
                          onClick={() => {
                            close();
                            if (isFollowed) onStopFollowing();
                            else onFollow(peer.connectionId);
                          }}
                        >
                          <IconEye size={16} />
                          {isFollowed ? 'Stop following' : 'Follow them'}
                        </button>
                        {isHost ? (
                          <button
                            type="button"
                            className="btn btn--sm btn--danger"
                            style={{ justifyContent: 'flex-start' }}
                            onClick={() => { close(); onKick(peer.user.id); }}
                          >
                            <IconLogout size={16} />
                            Remove from sketchpad
                          </button>
                        ) : null}
                        {!peer.viewport ? (
                          <p className="tiny muted" style={{ margin: 0 }}>
                            Waiting for them to move around before we know where they are.
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}

          {!alone ? (
            <>
              <div className="divider" style={{ margin: '12px 0' }} />
              <button
                type="button"
                className="btn btn--sm btn--ghost btn--block"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => { close(); onToggleOverview(); }}
              >
                <IconUsers size={16} />
                {overview ? 'Back to my view' : 'Show everyone on the canvas'}
              </button>
            </>
          ) : null}

          <p className="tiny muted" style={{ margin: '12px 0 0', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="status-dot" data-state={connection === 'open' ? 'open' : connection} />
            {connection === 'open'
              ? `Connected · about ${latency}ms away`
              : connection === 'reconnecting' ? 'Reconnecting…' : 'Offline'}
          </p>
        </div>
      ) : null}
    </div>
  );
}
